import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { currentMode, setModule } = useAuth();
  const [showClientCompanyModal, setShowClientCompanyModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showAllInvoicesModal, setShowAllInvoicesModal] = useState(false);
  const [showPendingApprovalsModal, setShowPendingApprovalsModal] = useState(false);

  const handleCreateClientCompany = (type: 'client' | 'company') => {
    setShowClientCompanyModal(false);
    if (type === 'client') {
      navigate('/create-client');
    } else {
      navigate('/create-company-account');
    }
  };

  const handleCreateInvoice = (type: 'revenue' | 'expense') => {
    setShowInvoiceModal(false);
    if (type === 'revenue') {
      window.open('/create-invoice', '_blank');
    } else {
      window.open('/create-expenses-invoice', '_blank');
    }
  };

  const handleAllInvoices = (type: 'revenue' | 'expenses') => {
    setShowAllInvoicesModal(false);
    setModule(type);
    if (type === 'revenue') {
      navigate('/all-invoices');
    } else {
      navigate('/all-expenses-invoices');
    }
  };

  const handlePendingApprovals = (type: 'revenue' | 'expenses') => {
    setShowPendingApprovalsModal(false);
    setModule(type);
    if (type === 'revenue') {
      navigate('/pending-revenue-approvals');
    } else {
      navigate('/pending-expense-approvals');
    }
  };

  const inputterCards = [
    {
      id: 'client-company',
      title: 'Create Client/Company',
      description: 'Add a new client or company account',
      icon: '🏢',
      bgGradient: 'from-blue-500/20 to-indigo-500/20',
      borderColor: 'border-blue-400/50',
      hoverBorderColor: 'hover:border-blue-400',
      onClick: () => setShowClientCompanyModal(true)
    },
    {
      id: 'invoice',
      title: 'Create Invoice',
      description: 'Create a revenue or expense invoice',
      icon: '📝',
      bgGradient: 'from-green-500/20 to-emerald-500/20',
      borderColor: 'border-green-400/50',
      hoverBorderColor: 'hover:border-green-400',
      onClick: () => setShowInvoiceModal(true)
    },
    {
      id: 'all-invoices',
      title: 'All Invoices',
      description: 'View all revenue or expense invoices',
      icon: '📋',
      bgGradient: 'from-blue-500/20 to-cyan-500/20',
      borderColor: 'border-blue-400/50',
      hoverBorderColor: 'hover:border-blue-400',
      onClick: () => setShowAllInvoicesModal(true)
    },
    {
      id: 'ledger',
      title: 'Create Ledger',
      description: 'Add a manual ledger entry',
      icon: '📚',
      bgGradient: 'from-purple-500/20 to-pink-500/20',
      borderColor: 'border-purple-400/50',
      hoverBorderColor: 'hover:border-purple-400',
      onClick: () => navigate('/create-ledger')
    },
    {
      id: 'manual-ledgers',
      title: 'Manual Ledgers',
      description: 'View and manage all manual ledgers',
      icon: '📖',
      bgGradient: 'from-amber-500/20 to-yellow-500/20',
      borderColor: 'border-amber-400/50',
      hoverBorderColor: 'hover:border-amber-400',
      onClick: () => navigate('/manual-ledgers')
    },
    {
      id: 'client-ledger',
      title: 'Client Ledgers',
      description: 'View and manage client ledgers',
      icon: '👥',
      bgGradient: 'from-cyan-500/20 to-blue-500/20',
      borderColor: 'border-cyan-400/50',
      hoverBorderColor: 'hover:border-cyan-400',
      onClick: () => navigate('/clients')
    },
    {
      id: 'company-ledger',
      title: 'Company Ledgers',
      description: 'View and manage company ledgers',
      icon: '🏭',
      bgGradient: 'from-orange-500/20 to-red-500/20',
      borderColor: 'border-orange-400/50',
      hoverBorderColor: 'hover:border-orange-400',
      onClick: () => navigate('/company-ledgers')
    },
    {
      id: 'reports',
      title: 'Reports',
      description: 'View financial reports and analytics',
      icon: '📈',
      bgGradient: 'from-teal-500/20 to-green-500/20',
      borderColor: 'border-teal-400/50',
      hoverBorderColor: 'hover:border-teal-400',
      onClick: () => {
        setModule(null);
        navigate('/reports');
      }
    }
  ];

  const authorizerCards = [
    {
      id: 'all-invoices',
      title: 'All Invoices',
      description: 'View all revenue or expense invoices',
      icon: '📋',
      bgGradient: 'from-blue-500/20 to-cyan-500/20',
      borderColor: 'border-blue-400/50',
      hoverBorderColor: 'hover:border-blue-400',
      onClick: () => setShowAllInvoicesModal(true)
    },
    {
      id: 'pending-approvals',
      title: 'Pending Approvals',
      description: 'Review pending revenue or expense approvals',
      icon: '✅',
      bgGradient: 'from-green-500/20 to-emerald-500/20',
      borderColor: 'border-green-400/50',
      hoverBorderColor: 'hover:border-green-400',
      onClick: () => setShowPendingApprovalsModal(true)
    },
    {
      id: 'user-management',
      title: 'User Management',
      description: 'Create and manage user accounts',
      icon: '👥',
      bgGradient: 'from-indigo-500/20 to-purple-500/20',
      borderColor: 'border-indigo-400/50',
      hoverBorderColor: 'hover:border-indigo-400',
      onClick: () => {
        setModule(null);
        navigate('/user-management');
      }
    },
    {
      id: 'reports',
      title: 'Reports',
      description: 'View financial reports and analytics',
      icon: '📈',
      bgGradient: 'from-purple-500/20 to-pink-500/20',
      borderColor: 'border-purple-400/50',
      hoverBorderColor: 'hover:border-purple-400',
      onClick: () => {
        setModule(null);
        navigate('/reports');
      }
    }
  ];

  const getCards = () => {
    if (currentMode === 'authorizer') {
      return authorizerCards;
    }
    return inputterCards;
  };

  const cards = getCards();

  return (
    <div className="min-h-screen relative">
      {/* Background Image with Blur */}
      <div 
        className="fixed inset-0 w-full h-full bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(/assets/background.png)',
          filter: 'blur(0px)',
          transform: 'scale(1.1)'
        }}
      />
      
      {/* Overlay to darken the background */}
      <div className="fixed inset-0 bg-gray-900 bg-opacity-30" />
      
      {/* Content */}
      <div className="relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-white mb-2">Dashboard</h1>
            <p className="text-white/80 font-semibold">Quick access to common actions</p>
          </div>

          {/* Cards Grid */}
          {cards.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {cards.map((card) => (
              <button
                key={card.id}
                onClick={card.onClick}
                className={`relative bg-white/70 backdrop-blur-lg border-2 ${card.borderColor} ${card.hoverBorderColor} rounded-xl p-6 text-left transition-all duration-300 hover:scale-105 hover:shadow-2xl overflow-hidden group`}
              >
                {/* Gradient overlay */}
                <div className={`absolute inset-0 bg-gradient-to-br ${card.bgGradient} transition-opacity duration-300 group-hover:opacity-80`} />
                
                {/* Content */}
                <div className="relative z-10">
                  <div className="flex items-start justify-between mb-4">
                    <div className="text-5xl">{card.icon}</div>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2 drop-shadow-lg">{card.title}</h3>
                  <p className="text-gray-800/90 text-sm drop-shadow-md">{card.description}</p>
                </div>
              </button>
              ))}
            </div>
          ) : (
            <div className="bg-white/70 backdrop-blur-lg border-2 border-gray-300 rounded-xl p-12 text-center">
              <div className="text-6xl mb-4">🚧</div>
              <h3 className="text-2xl font-semibold text-gray-800 mb-2">Dashboard</h3>
              <p className="text-gray-600">No content available.</p>
            </div>
          )}
        </div>
      </div>

      {/* Client/Company Modal */}
      {showClientCompanyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white/95 backdrop-blur-md rounded-xl shadow-2xl p-8 max-w-md w-full mx-4">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Create Account</h2>
            <p className="text-gray-600 mb-6">What type of account would you like to create?</p>
            <div className="space-y-3">
              <button
                onClick={() => handleCreateClientCompany('client')}
                className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-gray-800 font-semibold py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <span className="text-xl">👤</span>
                <span>Create Client</span>
              </button>
              <button
                onClick={() => handleCreateClientCompany('company')}
                className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-gray-800 font-semibold py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <span className="text-xl">🏢</span>
                <span>Create Company</span>
              </button>
              <button
                onClick={() => setShowClientCompanyModal(false)}
                className="w-full bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-3 px-4 rounded-lg transition-all duration-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Modal */}
      {showInvoiceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white/95 backdrop-blur-md rounded-xl shadow-2xl p-8 max-w-md w-full mx-4">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Create Invoice</h2>
            <p className="text-gray-600 mb-6">What type of invoice would you like to create?</p>
            <div className="space-y-3">
              <button
                onClick={() => handleCreateInvoice('revenue')}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-gray-800 font-semibold py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <span className="text-xl">💰</span>
                <span>Create Revenue Invoice</span>
              </button>
              <button
                onClick={() => handleCreateInvoice('expense')}
                className="w-full bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-gray-800 font-semibold py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <span className="text-xl">💸</span>
                <span>Create Expense Invoice</span>
              </button>
              <button
                onClick={() => setShowInvoiceModal(false)}
                className="w-full bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-3 px-4 rounded-lg transition-all duration-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* All Invoices Modal - Authorizer */}
      {showAllInvoicesModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white/95 backdrop-blur-md rounded-xl shadow-2xl p-8 max-w-md w-full mx-4">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">All Invoices</h2>
            <p className="text-gray-600 mb-6">Which type of invoices would you like to view?</p>
            <div className="space-y-3">
              <button
                onClick={() => handleAllInvoices('revenue')}
                className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <span className="text-xl">💰</span>
                <span>All Revenue Invoices</span>
              </button>
              <button
                onClick={() => handleAllInvoices('expenses')}
                className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <span className="text-xl">💸</span>
                <span>All Expenses Invoices</span>
              </button>
              <button
                onClick={() => setShowAllInvoicesModal(false)}
                className="w-full bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-3 px-4 rounded-lg transition-all duration-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pending Approvals Modal - Authorizer */}
      {showPendingApprovalsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white/95 backdrop-blur-md rounded-xl shadow-2xl p-8 max-w-md w-full mx-4">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Pending Approvals</h2>
            <p className="text-gray-600 mb-6">Which type of pending approvals would you like to review?</p>
            <div className="space-y-3">
              <button
                onClick={() => handlePendingApprovals('revenue')}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <span className="text-xl">✅</span>
                <span>Revenue Pending Approvals</span>
              </button>
              <button
                onClick={() => handlePendingApprovals('expenses')}
                className="w-full bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <span className="text-xl">✅</span>
                <span>Expenses Pending Approvals</span>
              </button>
              <button
                onClick={() => setShowPendingApprovalsModal(false)}
                className="w-full bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-3 px-4 rounded-lg transition-all duration-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;

